import {
  useContext,
  useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import round from 'lodash/round';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';
import { styled } from '@mui/material/styles';
import { formatMoneyValue } from '../../../../../util';
import { PortfolioSecurity } from '../../../../4-module/widgets/portfolio/components/interfaces';
import TaxRank from '../../../../4-module/widgets/portfolio/components/taxRank';
import { Box, Typography } from '../../../../1-primative';
import { TableCell, Tooltip } from '../../../../2-component';
import { CurrencyInput } from './currencyInput';
import {
  RebalanceAutofillContext,
  RebalanceDraftsContext,
  RebalanceOptimizedSecurities,
  RebalanceResetContext,
} from '../portfolioPage';

type ColumnData = {
  data: PortfolioSecurity,
};

type FooterData = {
  data: PortfolioSecurity[],
};

export type ColumnTradeDataLog = {
  securityId: string;
  subAccountId: string;
  value: number;
  postTrade: number;
};

type ColumnTradeData = {
  data: PortfolioSecurity;
  onChange?: (data: ColumnTradeDataLog) => void;
};

const SecurityColumn = ({ data }: ColumnData) => {
  const { ticker, financialProductTaxRank, translatedName } = data;

  return (<Box display='flex' justifyContent='start' alignItems='center' gap='4px'>
    {financialProductTaxRank && <TaxRank taxRank={financialProductTaxRank} />}

    <Typography>
      <Box display='flex' justifyContent='start' flexDirection='column'>
        <Tooltip title={translatedName}>{ticker}</Tooltip>

        {formatMoneyValue(data.currentPriceCents)}
      </Box>
    </Typography>
  </Box>);
};

const CurrentCashColumn = ({ data }: ColumnData) => (<Typography align='right'>{ formatMoneyValue(round(data.valueCents) || 0) }</Typography>);
const CurrentPercentColumn = ({ data }: ColumnData) => {
  const { expectedValueCents, valueCents } = data;

  const valuePercent = useMemo(() => {
    if (!isNumber(valueCents) || !isNumber(expectedValueCents) || expectedValueCents <= 0) { return 0; }
    const percentage = valueCents / expectedValueCents;
    return !isFinite(percentage || 0) ? '100.00' : round((percentage || 0) * 100, 2).toFixed(2);
  }, [expectedValueCents, valueCents]);

  return (<Typography align='right'>{ valuePercent }%</Typography>);
};
const CurrentHashColumn = ({ data }: ColumnData) => {
  const { t } = useTranslation(['rebalanceReportV2']);
  return (<Typography align='right'>{t('portfolio.currentHashColumn')}</Typography>);
};

const TargetCashColumn = ({ data }: ColumnData) => (<Typography align='right'>{ formatMoneyValue(round(data.expectedValueCents) || 0) }</Typography>);
const TargetPercentColumn = ({ data }: ColumnData) => (<Typography align='right'>{ round((data.expectedPercentage || 0) * 100, 2).toFixed(2) }%</Typography>);
const TargetHashColumn = ({ data }: ColumnData) => {
  const { t } = useTranslation(['rebalanceReportV2']);
  return (<Typography align='right'>{t('portfolio.targetHashColumn')}</Typography>);
};

const DriftCashColumn = ({ data }: ColumnData) => {
  const {
    expectedValueCents, valueCents, id, subAccounts,
  } = data;

  const valueCash = useMemo(() => {
    if (id !== 'CASH') { return round(valueCents - expectedValueCents) || 0; }
    const drift: number = subAccounts.reduce((total: number, subAccount) => total + (subAccount.cashCents - subAccount.cashOnHoldToTradeCents), 0);
    return (round(valueCents - expectedValueCents) || 0) + drift;
  }, [expectedValueCents, valueCents, id, subAccounts]);

  return (
    <Typography align='right'>{ formatMoneyValue(valueCash) }</Typography>
  );
};

const DriftPercentColumn = ({ data }: ColumnData) => {
  const { expectedValueCents, valueCents } = data;

  const valuePercent = useMemo(() => {
    if (!isNumber(valueCents) || !isNumber(expectedValueCents)) { return 0; }
    const percentage = (valueCents - expectedValueCents) / expectedValueCents;
    return !isFinite(percentage || 0) ? '100.00' : round((percentage || 0) * 100, 2).toFixed(2);
  }, [expectedValueCents, valueCents]);

  return (<Typography align='right'>{ valuePercent }%</Typography>);
};

const CurrentSubAccount = ({ data }: ColumnData) => {
  const { id } = data;

  const valueCash = useMemo(() => {
    if (id !== 'CASH') { return !isEmpty(data.subAccounts) ? data.subAccounts[0].expectedCashCents - data.subAccounts[0].cashOnHoldToTradeCents : 0; }
    return !isEmpty(data.subAccounts) ? data.subAccounts[0].cashCents - data.subAccounts[0].cashOnHoldToTradeCents : 0;
  }, [data, id]);

  return (<Typography align='right'>{ formatMoneyValue(valueCash) }</Typography>);
};

const TradeTableCell = styled(TableCell)({
  position: 'relative',
  padding: 0,
});

const TradeSubAccount = ({ data, onChange }: ColumnTradeData) => {
  const { t } = useTranslation(['rebalanceReportV2']);
  const resetInput = useContext(RebalanceResetContext);
  const autofillContext = useContext(RebalanceAutofillContext);
  const optimizerContext = useContext(RebalanceOptimizedSecurities);

  const [inputVal, setInputVal] = useState<number>(0);

  const currentValue = useMemo(() => (
    !isEmpty(data.subAccounts) ? data.subAccounts[0].expectedCashCents - data.subAccounts[0].cashOnHoldToTradeCents : 0
  ), [data]);

  const targetValue = useMemo(() => ((data.expectedValueCents || 0) / 100) - (currentValue / 100), [data, currentValue]);
  const negativeMax = useMemo(() => (currentValue / 100) * -1, [currentValue]);

  const optimizedValue = useMemo((): PortfolioSecurity | null => (
    optimizerContext.find((item: PortfolioSecurity) => item.id === data.id) || null
  ), [optimizerContext, data.id]);

  useEffect(() => {
    if (resetInput) { setInputVal(0); }
  }, [resetInput]);

  useEffect(() => {
    if (autofillContext) { setInputVal(targetValue); }
  }, [autofillContext, targetValue]);

  useEffect(() => {
    if (optimizedValue) {
      if (!isEmpty(optimizedValue.subAccounts[0]?.optimalHolding)) {
        const value: number = optimizedValue.subAccounts[0].optimalHolding.expectedValueCents / 100;
        const current: number = (currentValue / 100);
        setInputVal(value - current);
      } else { setInputVal(inputVal); }
    }
  }, [optimizedValue, currentValue, inputVal]);

  const postTrade: number = useMemo(() => {
    const value = Number(Number((inputVal || 0)).toFixed(2).replace('.', ''));
    return value + currentValue;
  }, [inputVal, currentValue]);

  const handleInputChange = (result: number) => {
    setInputVal(result);
    if (onChange) {
      onChange({
        securityId: data.id,
        subAccountId: !isEmpty(data.subAccounts) ? data.subAccounts[0].id : '',
        value: Number(result),
        postTrade,
      });
    }
  };

  return (
    <>
      { data.id !== 'CASH' ? (
        <CurrencyInput
          value={inputVal}
          placeholder={t('portfolio.enterBuySell')}
          negativeMax={negativeMax}
          onChange={handleInputChange} />
      ) : <Typography align='right'>-</Typography> }
    </>
  );
};

const PostSubAccount = ({ data }: ColumnData) => {
  const draftsContext = useContext(RebalanceDraftsContext);

  const draftBySecurity: ColumnTradeDataLog | undefined = useMemo(() => {
    const subAccountId = !isEmpty(data.subAccounts) ? data.subAccounts[0].id : '';
    return draftsContext.find((draft) => draft.securityId === data.id && draft.subAccountId === subAccountId);
  }, [draftsContext, data]);

  const currentValue = useMemo(() => (
    !isEmpty(data.subAccounts) ? round(data.subAccounts[0].expectedCashCents - data.subAccounts[0].cashOnHoldToTradeCents) : 0
  ), [data]);

  const currentTrade = useMemo(() => (
    !isEmpty(draftBySecurity) ? Number((draftBySecurity?.value || 0).toFixed(2).replace('.', '')) : 0
  ), [draftBySecurity]);

  return (
    <>
      { (data.id !== 'CASH' && (draftBySecurity?.value || 0) !== 0) ? (
        <Typography align='right'>{ formatMoneyValue(currentValue + currentTrade) }</Typography>
      ) : <Typography align='right'>-</Typography> }
    </>
  );
};

const FooterPortfolio = ({ data }: FooterData) => <div>Table Footer</div>;

export {
  SecurityColumn, CurrentCashColumn, CurrentPercentColumn, CurrentHashColumn, TargetCashColumn, TargetPercentColumn,
  TargetHashColumn, DriftCashColumn, DriftPercentColumn, CurrentSubAccount, TradeSubAccount, PostSubAccount,
  TradeTableCell, FooterPortfolio,
};
